import URL from 'url-parse';
import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { intlShape } from 'react-intl';
import Cookies from 'universal-cookie';
import fonts from '@redbubble/design-system/styles/fonts';
import { compose } from 'redux';
import config from '../../../config';
import { publicAssetPath } from '../../lib/routing';
import messages from './messages';
import { dataLayer, buildGtmData } from '../../lib/googleTagManager';
import { buildUserProperties } from '../../lib/gtm/utils';
import { withUserAccount } from '../../containers/apollo';
import withRbVisitorID from '../../containers/redux/withRbVisitorID';

const cdnBaseUrl = config('cdnBaseUrl');

export class Head extends React.Component {
  render() {
    const {
      locale,
      intl,
      userInfo,
      cookies,
      userAccount,
      rbVisitorID
    } = this.props;

    const title = intl.formatMessage(messages.Title);
    const description = intl.formatMessage(messages.Description);

    const gtmDataLayer = userInfo ? buildGtmData(userInfo, cookies) : {};

    const gtmUserProperties = buildUserProperties({
      userInfo,
      cookies,
      userAccount,
      rbVisitorID
    });

    return (
      <Helmet titleTemplate={config('htmlPage.titleTemplate')}>
        <html lang={locale} />
        <title>{title}</title>

        {fonts.map(({ name, path }) => {
          const pathName = new URL(path).pathname;

          return (
            config('preloadableFonts').includes(name) && (
              <link
                rel="preload"
                as="font"
                href={new URL(pathName, cdnBaseUrl).toString()}
                key={path}
                type="font/woff2"
                crossOrigin="anonymous"
              />
            )
          );
        })}

        <meta charSet="utf-8" />
        <meta name="application-name" content={title} />
        <meta name="description" content={description} />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=0"
        />
        <meta name="theme-color" content="#f2f2f6" />
        <meta
          name="msapplication-config"
          content={publicAssetPath('browserconfig.xml')}
        />

        <link rel="manifest" href={publicAssetPath('manifest.json')} />

        <link
          rel="apple-touch-icon-precomposed"
          sizes="152x152"
          href={publicAssetPath('favicons/apple-touch-icon-152x152.png')}
        />
        <link
          rel="apple-touch-icon-precomposed"
          sizes="144x144"
          href={publicAssetPath('favicons/apple-touch-icon-144x144.png')}
        />
        <link
          rel="apple-touch-icon-precomposed"
          sizes="120x120"
          href={publicAssetPath('favicons/apple-touch-icon-120x120.png')}
        />
        <link
          rel="apple-touch-icon-precomposed"
          sizes="114x114"
          href={publicAssetPath('favicons/apple-touch-icon-114x114.png')}
        />
        <link
          rel="apple-touch-icon-precomposed"
          sizes="76x76"
          href={publicAssetPath('favicons/apple-touch-icon-76x76.png')}
        />
        <link
          rel="apple-touch-icon-precomposed"
          sizes="72x72"
          href={publicAssetPath('favicons/apple-touch-icon-72x72.png')}
        />
        <link
          rel="apple-touch-icon-precomposed"
          sizes="57x57"
          href={publicAssetPath('favicons/apple-touch-icon-57x57.png')}
        />
        <link
          rel="apple-touch-icon-precomposed"
          sizes="60x60"
          href={publicAssetPath('favicons/apple-touch-icon-60x60.png')}
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={publicAssetPath('favicons/apple-touch-icon-180x180.png')}
        />
        <link
          rel="mask-icon"
          href={publicAssetPath('favicons/favicon-pinned-tab.svg')}
          color="#e41421"
        />
        <link
          rel="shortcut icon"
          href={publicAssetPath('favicons/favicon.ico')}
        />
        <link
          rel="icon"
          type="image/png"
          href={publicAssetPath('favicons/favicon-196x196.png')}
          sizes="196x196"
        />
        <link
          rel="icon"
          type="image/png"
          href={publicAssetPath('favicons/favicon-128x128.png')}
          sizes="128x128"
        />
        <link
          rel="icon"
          type="image/png"
          href={publicAssetPath('favicons/favicon-96x96.png')}
          sizes="96x96"
        />
        <link
          rel="icon"
          type="image/png"
          href={publicAssetPath('favicons/favicon-32x32.png')}
          sizes="32x32"
        />
        <link
          rel="icon"
          type="image/png"
          href={publicAssetPath('favicons/favicon-16x16.png')}
          sizes="16x16"
        />

        {/* Open Search Support */}
        <link
          rel="search"
          type="application/opensearchdescription+xml"
          title="Redbubble Search"
          href={publicAssetPath('openSearch/redbubble-202202.xml')}
        />

        <script>{dataLayer(gtmDataLayer, gtmUserProperties)}</script>
      </Helmet>
    );
  }
}

Head.displayName = 'Head';

Head.propTypes = {
  userInfo: PropTypes.shape({
    experiments: PropTypes.arrayOf(
      PropTypes.shape({
        experiment: PropTypes.string,
        group: PropTypes.string
      })
    ),
    enrolments: PropTypes.arrayOf(
      PropTypes.shape({
        experiment: PropTypes.string,
        group: PropTypes.string
      })
    ),
    country: PropTypes.string,
    currency: PropTypes.string,
    locale: PropTypes.string,
    federatedId: PropTypes.string
  }),
  intl: intlShape.isRequired,
  locale: PropTypes.string,
  cookies: PropTypes.instanceOf(Cookies)
};

Head.defaultProps = {
  locale: 'en'
};

const WrappedHead = compose(
  withUserAccount,
  withRbVisitorID
)(Head);

export default WrappedHead;
